/**
 * NEW ARRIVALS SECTION JAVASCRIPT
 * ===============================
 * Modern, clean JavaScript for new arrivals section interactions
 * Enterprise-grade component with error handling and advanced features
 */

class NewArrivals {
  constructor() {
    this.section = document.querySelector('.new-arrivals-section');
    if (!this.section) {
      console.warn('NewArrivals: Section not found');
      return;
    }
    
    // Core elements
    this.container = document.getElementById('new-arrivals-container');
    this.prevBtn = this.section.querySelector('.new-arrivals-nav-bar .na-nav-prev');
    this.nextBtn = this.section.querySelector('.new-arrivals-nav-bar .na-nav-next');
    this.grid = document.getElementById('new-arrivals-grid');
    
    // Configuration
    this.scrollAmount = 300; // Default: 280px card + 20px gap
    this.isScrolling = false;
    this.autoScrollEnabled = false;
    this.autoScrollInterval = null;
    this.intersectionObserver = null;
    
    // State management
    this.state = {
      currentIndex: 0,
      totalProducts: 0,
      isVisible: false,
      hasError: false
    };
    
    // Validate required elements
    if (!this.validateElements()) {
      console.error('NewArrivals: Required elements not found');
      return;
    }
    
    this.init();
  }

  /**
   * Validate that all required elements exist
   */
  validateElements() {
    const requiredElements = {
      container: this.container,
      prevBtn: this.prevBtn,
      nextBtn: this.nextBtn,
      grid: this.grid
    };
    
    for (const [name, element] of Object.entries(requiredElements)) {
      if (!element) {
        console.error(`NewArrivals: Missing required element: ${name}`);
        return false;
      }
    }
    
    return true;
  }

  init() {
    try {
      this.calculateProductCount();
      this.setupEventListeners();
      this.setupAccessibility();
      this.setupIntersectionObserver();
      this.setupLazyProducts(); // Lazy load products after 8th item
      this.setupAutoScroll();
      this.setupAdvancedKeyboard();
      this.setupAdvancedTouch();
      this.updateNavigationState();
      this.addLoadingStates();
      
      // Mark as successfully initialized
      this.section.classList.add('new-arrivals-initialized');
      
      console.log('NewArrivals: Successfully initialized with advanced features');
    } catch (error) {
      console.error('NewArrivals: Initialization failed', error);
      this.handleError(error);
    }
  }

  /**
   * Calculate total number of products
   */
  calculateProductCount() {
    const productCards = this.grid.querySelectorAll('.new-arrivals-card-wrapper');
    this.state.totalProducts = productCards.length;
    
    if (this.state.totalProducts === 0) {
      console.warn('NewArrivals: No products found');
      this.showEmptyState();
    }
  }

  /**
   * Show empty state when no products are available
   */
  showEmptyState() {
    const emptyState = document.createElement('div');
    emptyState.className = 'new-arrivals-empty-state';
    emptyState.innerHTML = `
      <div class="new-arrivals-empty-content">
        <p>No new arrivals available at the moment.</p>
        <p>Check back soon for fresh drops!</p>
      </div>
    `;
    
    this.grid.appendChild(emptyState);
    
    // Hide navigation when empty
    if (this.prevBtn) this.prevBtn.style.display = 'none';
    if (this.nextBtn) this.nextBtn.style.display = 'none';
  }

  /**
   * Setup event listeners using modern event delegation
   */
  setupEventListeners() {
    if (!this.container || !this.prevBtn || !this.nextBtn) return;
    
    // Navigation button events
    this.prevBtn.addEventListener('click', () => this.scrollPrevious());
    this.nextBtn.addEventListener('click', () => this.scrollNext());
    
    // Scroll events for navigation state
    this.container.addEventListener('scroll', () => this.updateNavigationState());
    
    // Touch/swipe support for mobile
    this.setupTouchEvents();
    
    // Keyboard navigation
    this.setupKeyboardNavigation();
    
    // Window resize handling
    window.addEventListener('resize', () => this.handleResize());
  }

  /**
   * Handle previous navigation
   */
  scrollPrevious() {
    if (!this.container || this.isScrolling) return;
    
    this.isScrolling = true;
    this.pauseAutoScroll();
    
    try {
      this.container.scrollBy({ 
        left: -this.scrollAmount, 
        behavior: 'smooth' 
      });
      
      this.state.currentIndex = Math.max(0, this.state.currentIndex - 1);
      this.trackNavigation('previous');
      
      // Re-enable scrolling after animation
      setTimeout(() => {
        this.isScrolling = false;
        this.resumeAutoScroll();
      }, 300);
      
    } catch (error) {
      console.error('NewArrivals: Scroll previous failed', error);
      this.isScrolling = false;
    }
  }

  /**
   * Handle next navigation
   */
  scrollNext() {
    if (!this.container || this.isScrolling) return;
    
    this.isScrolling = true;
    this.pauseAutoScroll();
    
    try {
      this.container.scrollBy({ 
        left: this.scrollAmount, 
        behavior: 'smooth' 
      });
      
      const maxIndex = Math.max(0, this.state.totalProducts - this.getVisibleCardCount());
      this.state.currentIndex = Math.min(maxIndex, this.state.currentIndex + 1);
      this.trackNavigation('next');
      
      // Re-enable scrolling after animation
      setTimeout(() => {
        this.isScrolling = false;
        this.resumeAutoScroll();
      }, 300);
      
    } catch (error) {
      console.error('NewArrivals: Scroll next failed', error);
      this.isScrolling = false;
    }
  }

  /**
   * Get number of visible cards based on viewport
   */
  getVisibleCardCount() {
    const viewportWidth = window.innerWidth;
    
    if (viewportWidth <= 749) return 2;      // Mobile
    if (viewportWidth <= 989) return 3;     // Tablet
    return 4;                               // Desktop
  }

  /**
   * Update navigation button states based on scroll position
   */
  updateNavigationState() {
    if (!this.container || !this.prevBtn || !this.nextBtn) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = this.container;
    const isAtStart = scrollLeft <= 0;
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1;
    
    // Update button states
    this.prevBtn.disabled = isAtStart;
    this.nextBtn.disabled = isAtEnd;
    
    // Update ARIA attributes
    this.prevBtn.setAttribute('aria-disabled', isAtStart);
    this.nextBtn.setAttribute('aria-disabled', isAtEnd);
    
    // Update visual states
    this.prevBtn.classList.toggle('disabled', isAtStart);
    this.nextBtn.classList.toggle('disabled', isAtEnd);
  }

  /**
   * Setup touch events for mobile swipe support
   */
  setupTouchEvents() {
    if (!this.container) return;
    
    let startX = 0;
    let scrollStart = 0;
    
    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      scrollStart = this.container.scrollLeft;
    }, { passive: true });
    
    this.container.addEventListener('touchmove', (e) => {
      if (!startX) return;
      
      const currentX = e.touches[0].clientX;
      const diff = startX - currentX;
      this.container.scrollLeft = scrollStart + diff;
    }, { passive: true });
    
    this.container.addEventListener('touchend', () => {
      startX = 0;
      scrollStart = 0;
    }, { passive: true });
  }

  /**
   * Setup keyboard navigation
   */
  setupKeyboardNavigation() {
    if (!this.section) return;
    
    this.section.addEventListener('keydown', (e) => {
      if (e.target.closest('.new-arrivals-nav')) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.target.click();
        }
      }
      
      // Arrow key navigation when focused on container
      if (e.target === this.container) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.scrollPrevious();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.scrollNext();
        }
      }
    });
  }

  /**
   * Setup intersection observer for performance
   */
  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      console.warn('NewArrivals: IntersectionObserver not supported');
      return;
    }
    
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.state.isVisible = entry.isIntersecting;
        
        if (entry.isIntersecting) {
          this.onSectionVisible();
        } else {
          this.onSectionHidden();
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '50px'
    });
    
    this.intersectionObserver.observe(this.section);
  }

  /**
   * Setup lazy loading for products (first 8 immediate, rest deferred)
   * Performance optimization to reduce initial DOM size
   */
  setupLazyProducts() {
    if (!this.container || !this.grid) return;

    const lazyProducts = this.grid.querySelectorAll('[data-lazy-product="true"]');
    if (lazyProducts.length === 0) return;

    // Track revealed products
    const revealedProducts = new Set();

    const revealProduct = (product) => {
      if (revealedProducts.has(product)) return;
      
      revealedProducts.add(product);
      product.style.visibility = 'visible';
      product.removeAttribute('data-lazy-product');
      
      // Smooth fade-in
      product.style.opacity = '0';
      product.style.transition = 'opacity 0.3s ease-in-out';
      requestAnimationFrame(() => {
        product.style.opacity = '1';
      });
    };

    // Use Intersection Observer for viewport-based lazy loading
    if ('IntersectionObserver' in window) {
      const lazyObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            revealProduct(entry.target);
            lazyObserver.unobserve(entry.target);
          }
        });
      }, {
        root: this.container,
        rootMargin: '400px', // Load 400px before viewport
        threshold: 0.01
      });

      lazyProducts.forEach(product => {
        lazyObserver.observe(product);
      });

      // Also reveal on scroll
      let revealTimeout;
      this.container.addEventListener('scroll', () => {
        clearTimeout(revealTimeout);
        revealTimeout = setTimeout(() => {
          const unrevealed = this.grid.querySelectorAll('[data-lazy-product="true"]');
          const batchSize = Math.min(4, unrevealed.length);
          for (let i = 0; i < batchSize; i++) {
            if (unrevealed[i]) {
              revealProduct(unrevealed[i]);
            }
          }
        }, 200);
      }, { passive: true });

    } else {
      // Fallback: reveal all immediately
      lazyProducts.forEach(product => revealProduct(product));
    }

    console.log(`NewArrivals: Lazy loading initialized for ${lazyProducts.length} products`);
  }

  /**
   * Handle section becoming visible
   */
  onSectionVisible() {
    // Lazy load images if needed
    this.lazyLoadImages();
    
    // Start auto-scroll if enabled
    if (this.autoScrollEnabled) {
      this.startAutoScroll();
    }
    
    // Track section view
    this.trackSectionView();
  }

  /**
   * Handle section becoming hidden
   */
  onSectionHidden() {
    // Pause auto-scroll when not visible
    this.pauseAutoScroll();
  }

  /**
   * Setup auto-scroll functionality
   */
  setupAutoScroll() {
    // Auto-scroll is disabled by default, can be enabled via data attribute
    const autoScrollAttr = this.section.dataset.autoScroll;
    this.autoScrollEnabled = autoScrollAttr === 'true';
    
    // Get auto-scroll speed from data attribute
    const autoScrollSpeed = parseInt(this.section.dataset.autoScrollSpeed) || 4;
    this.autoScrollDelay = autoScrollSpeed * 1000; // Convert to milliseconds
    
    if (this.autoScrollEnabled) {
      // Pause auto-scroll on hover
      this.section.addEventListener('mouseenter', () => this.pauseAutoScroll());
      this.section.addEventListener('mouseleave', () => this.resumeAutoScroll());
      
      // Pause auto-scroll on focus (accessibility)
      this.section.addEventListener('focusin', () => this.pauseAutoScroll());
      this.section.addEventListener('focusout', () => this.resumeAutoScroll());
    }
  }

  /**
   * Start auto-scroll
   */
  startAutoScroll() {
    if (!this.autoScrollEnabled || this.autoScrollInterval) return;
    
    this.autoScrollInterval = setInterval(() => {
      if (this.state.isVisible && !this.isScrolling) {
        // Check if we're at the end
        const { scrollLeft, scrollWidth, clientWidth } = this.container;
        const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1;
        
        if (isAtEnd) {
          // Reset to beginning
          this.container.scrollTo({ left: 0, behavior: 'smooth' });
          this.state.currentIndex = 0;
        } else {
          this.scrollNext();
        }
      }
    }, this.autoScrollDelay || 4000); // Use configured delay
  }

  /**
   * Pause auto-scroll
   */
  pauseAutoScroll() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  }

  /**
   * Resume auto-scroll
   */
  resumeAutoScroll() {
    if (this.autoScrollEnabled && this.state.isVisible) {
      this.startAutoScroll();
    }
  }

  /**
   * Lazy load images for performance
   */
  lazyLoadImages() {
    const images = this.section.querySelectorAll('img[data-src]');
    
    images.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        img.classList.add('loaded');
      }
    });
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      try {
        this.updateNavigationState();
        this.adjustScrollAmount();
        this.calculateProductCount();
      } catch (error) {
        console.error('NewArrivals: Resize handling failed', error);
      }
    }, 250);
  }

  /**
   * Adjust scroll amount based on viewport
   */
  adjustScrollAmount() {
    const viewportWidth = window.innerWidth;
    
    if (viewportWidth <= 749) {
      // Mobile: scroll by card width + gap
      this.scrollAmount = 200; // Approximate mobile card width
    } else if (viewportWidth <= 989) {
      // Tablet: scroll by card width + gap
      this.scrollAmount = 260; // Approximate tablet card width
    } else {
      // Desktop: scroll by card width + gap
      this.scrollAmount = 300; // Desktop card width + gap
    }
  }

  /**
   * Setup accessibility features
   */
  setupAccessibility() {
    // Accessibility attributes are defined in Liquid markup.
    // No JS-driven ARIA/role/tabindex or injected focus CSS.
  }

  /**
   * Add focus styles for better keyboard accessibility
   */
  addFocusStyles() { /* no-op: focus styles live in CSS */ }

  /**
   * Add loading states for better UX - Simplified to not hide images
   */
  addLoadingStates() {
    // Check if loading states are disabled
    if (this.section.dataset.noLoading === 'true') {
      return;
    }
    
    // Don't add loading class that hides images
    // Just ensure images are visible
    const images = this.section.querySelectorAll('img');
    images.forEach(img => {
      img.classList.add('loaded'); // Ensure images are visible
    });
  }

  /**
   * Handle errors gracefully
   */
  handleError(error) {
    this.state.hasError = true;
    this.section.classList.add('new-arrivals-error');
    
    // Create error message
    const errorMessage = document.createElement('div');
    errorMessage.className = 'new-arrivals-error-message';
    errorMessage.innerHTML = `
      <div class="new-arrivals-error-content">
        <p>Something went wrong loading the new arrivals.</p>
        <button class="new-arrivals-retry-btn" type="button">
          Try Again
        </button>
      </div>
    `;
    
    // Insert error message
    this.section.appendChild(errorMessage);
    const retryBtn = errorMessage.querySelector('.new-arrivals-retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => window.location.reload());
    }
    
    // Hide navigation on error
    if (this.prevBtn) this.prevBtn.style.display = 'none';
    if (this.nextBtn) this.nextBtn.style.display = 'none';
  }

  /**
   * Track section view for analytics
   */
  trackSectionView() {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'section_view', {
        'section_name': 'new_arrivals',
        'products_count': this.state.totalProducts
      });
    }
  }

  /**
   * Track navigation interactions for analytics
   */
  trackNavigation(direction) {
    try {
      // Enhanced analytics tracking
      const trackingData = {
        direction: direction,
        section: 'new_arrivals',
        current_index: this.state.currentIndex,
        total_products: this.state.totalProducts,
        viewport_width: window.innerWidth
      };
      
      // Google Analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'new_arrivals_navigation', trackingData);
      }
      
      // Facebook Pixel
      if (typeof fbq !== 'undefined') {
        fbq('track', 'ViewContent', {
          content_type: 'product_group',
          content_category: 'new_arrivals'
        });
      }
      
      // Custom analytics
      if (window.customAnalytics && typeof window.customAnalytics.track === 'function') {
        window.customAnalytics.track('new_arrivals_navigation', trackingData);
      }
      
      // GTM dataLayer
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'new_arrivals_navigation',
          ...trackingData
        });
      }
      
    } catch (error) {
      console.warn('NewArrivals: Analytics tracking failed', error);
    }
  }

  /**
   * Public method to refresh the component
   */
  refresh() {
    try {
      this.calculateProductCount();
      this.updateNavigationState();
      this.adjustScrollAmount();
      console.log('NewArrivals: Component refreshed');
    } catch (error) {
      console.error('NewArrivals: Refresh failed', error);
    }
  }

  /**
   * Public method to scroll to a specific product
   */
  scrollToProduct(index) {
    if (!this.container || index < 0 || index >= this.state.totalProducts) {
      console.warn('NewArrivals: Invalid product index', index);
      return;
    }
    
    try {
      const cardWidth = this.scrollAmount;
      const targetScroll = index * cardWidth;
      
      this.container.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
      
      this.state.currentIndex = index;
      this.trackNavigation(`scroll_to_${index}`);
      
    } catch (error) {
      console.error('NewArrivals: Scroll to product failed', error);
    }
  }

  /**
   * Public method to enable/disable auto-scroll
   */
  setAutoScroll(enabled) {
    this.autoScrollEnabled = enabled;
    
    if (enabled && this.state.isVisible) {
      this.startAutoScroll();
    } else {
      this.pauseAutoScroll();
    }
  }

  /**
   * Public method to get current state
   */
  getState() {
    return { ...this.state };
  }



  /**
   * Add advanced keyboard shortcuts
   */
  setupAdvancedKeyboard() {
    document.addEventListener('keydown', (e) => {
      // Only handle if section is focused or visible
      if (!this.state.isVisible && !this.section.contains(document.activeElement)) {
        return;
      }
      
      switch (e.key) {
        case 'Home':
          e.preventDefault();
          this.scrollToProduct(0);
          break;
        case 'End':
          e.preventDefault();
          this.scrollToProduct(this.state.totalProducts - 1);
          break;
        case 'PageUp':
          e.preventDefault();
          this.scrollPrevious();
          break;
        case 'PageDown':
          e.preventDefault();
          this.scrollNext();
          break;
      }
    });
  }

  /**
   * Add touch gesture support
   */
  setupAdvancedTouch() {
    if (!this.container) return;
    
    let startX = 0;
    let startY = 0;
    let distX = 0;
    let distY = 0;
    let threshold = 50;
    let restraint = 100;
    
    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    
    this.container.addEventListener('touchmove', (e) => {
      distX = e.touches[0].clientX - startX;
      distY = e.touches[0].clientY - startY;
    }, { passive: true });
    
    this.container.addEventListener('touchend', () => {
      if (Math.abs(distX) >= threshold && Math.abs(distY) <= restraint) {
        if (distX > 0) {
          this.scrollPrevious();
        } else {
          this.scrollNext();
        }
      }
      
      // Reset values
      startX = startY = distX = distY = 0;
    }, { passive: true });
  }

  /**
   * Public method to destroy the component
   */
  destroy() {
    try {
      // Clear intervals
      this.pauseAutoScroll();
      
      // Clear timeouts
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      // Disconnect observers
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      
      // Remove event listeners
      window.removeEventListener('resize', () => this.handleResize());
      

      
      // Remove classes
      this.section.classList.remove('new-arrivals-initialized', 'new-arrivals-loading', 'new-arrivals-error');
      
      console.log('NewArrivals: Component destroyed');
      
    } catch (error) {
      console.error('NewArrivals: Destroy failed', error);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.newArrivals = new NewArrivals();
});

// Re-initialize on theme editor updates (Shopify specific)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.new-arrivals-section')) {
    window.newArrivals = new NewArrivals();
  }
});

// Handle section unload
document.addEventListener('shopify:section:unload', (event) => {
  if (event.target.querySelector('.new-arrivals-section')) {
    if (window.newArrivals && window.newArrivals.resizeTimeout) {
      clearTimeout(window.newArrivals.resizeTimeout);
    }
  }
});